import * as React from "react";
import {useCallback, useMemo, useRef, useState} from "react";
import EmailEditor, {EditorRef, EmailEditorProps} from "react-email-editor";
import {Box, Button, Stack, TextField} from "@mui/material";
import {toast} from "react-toastify";

export type HtmlEditorProps = {
  id?: string;
  design?: string | Record<string, unknown>;
  title?: string;
  onSave: (title: string, html: string, json: string, id?: string) => void;
};

const HtmlTemplateEditor: React.FC<Readonly<HtmlEditorProps>> = ({
                                                                   id,
                                                                   design,
                                                                   title: initialTitle = "",
                                                                   onSave,
                                                                 }) => {
  const emailEditorRef = useRef<EditorRef>(null);
  const [title, setTitle] = useState<string>(initialTitle);
  const [isReady, setIsReady] = useState(false);

  const titleError = title.trim() === "";

  const parsedDesign = useMemo(() => {
    if (!design) return undefined;
    try {
      return typeof design === "string" ? JSON.parse(design) : design;
    } catch {
      toast.error("Invalid template design JSON.");
      return undefined;
    }
  }, [design]);

  const saveDesign = useCallback(() => {
    if (titleError || !isReady) return;
    const editor = emailEditorRef.current?.editor;
    if (!editor) return;

    editor.exportHtml(({design, html}) => {
      onSave(title.trim(), html, JSON.stringify(design), id);
    });
  }, [id, isReady, onSave, title, titleError]);

  const onReady: EmailEditorProps["onReady"] = (unlayer: any) => {
    setIsReady(true);

    // Image upload hook
    unlayer.registerCallback(
      "image",
      (file: any, done: (result: { progress: number; url: string }) => void) => {
        // TODO: fix it , this part is never called....
        console.log("onReady.image :", file);
        // TODO: upload file.attachments[0] to your backend and use its URL
        done({progress: 100, url: "https://bulk-mail.amoustms.app/logo.png"});
      }
    );

    unlayer.registerCallback('image:removed', function (image: any, done: any) {
      // image will include id, userId and projectId
      console.log("removed image", image);
      // call this when image has been deleted
      done();
    });

    if (parsedDesign) {
      unlayer.loadDesign(parsedDesign);
    }
  };

  return (
    <Box>
      <Stack direction={{xs: "column", sm: "row"}} spacing={2} sx={{mb: 2}}>
        <TextField
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          error={titleError}
          helperText={titleError ? "Title is required" : ""}
          sx={{width: "100ch"}}
        />
        <Button variant="contained" onClick={saveDesign} disabled={titleError || !isReady}>
          Save
        </Button>
      </Stack>

      <EmailEditor
        ref={emailEditorRef}
        onReady={onReady}
        minHeight="600px"
        style={{border: "1px solid #e0e0e0", borderRadius: 8}}
      />
    </Box>
  );
};

export default HtmlTemplateEditor;
